import { useEffect, useCallback, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2, AlertCircle } from 'lucide-react'
import { Button, toast } from '@atrvd/saas-ui'
import { useQueryClient } from '@tanstack/react-query'
import { useLedgerSyncConnectionStatus, LEDGERSYNC_KEYS } from '@/hooks/useLedgerSync'
import { BANKING_KEYS } from '@/lib/query-keys'
import { useOrganization } from '@/contexts/OrganizationContext'
import {
  getLedgerSyncOAuthState,
  clearLedgerSyncOAuthState,
} from '@/lib/ledgersync-oauth'
import { lsDebug, lsDebugError } from '@/lib/ledgersync-debug'

/**
 * Maximum time to wait for the async `connection.active` event to finalise
 * the connection before showing a "still processing" message (90s).
 */
const FINALIZE_TIMEOUT_MS = 90_000

/**
 * Redirect status values that indicate a successful widget completion.
 * LedgerSync v3 appends `?status=connected` on success; treat both
 * 'connected' and 'success' as successful (case-insensitive) for resilience.
 */
const SUCCESS_STATUSES = new Set(['connected', 'success'])

/**
 * Callback page for the LedgerSync v3 connect flow (DCP-216).
 *
 * The hosted Connect widget redirects here after the user authorises their
 * institution. The redirect URL looks like:
 *   https://<host>/ledgersync-callback?status=connected&connection_id=con_<uuid>
 *
 * This page reads `status` from the query string to detect errors early, then
 * polls the `connection-status` edge function (which finalises the connection
 * server-side by pulling accounts) until the connection reaches a terminal v3
 * state (`active` / `failed` / `disconnected`), then returns to the stored
 * `returnPath` — `/settings/financial-accounts` by default (DCP-324) — with a
 * success/error toast.
 *
 * The `connection.active` webhook is a secondary / faster path — if it fires
 * first, the poll will immediately observe the terminal state on the next tick.
 */
export function LedgerSyncCallbackPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()

  // Read persisted connect state once on mount.
  const [{ connectionId, returnPath }] = useState(() => {
    const state = getLedgerSyncOAuthState()
    return state
  })

  // Read the redirect query params once on mount.
  // `redirectStatus` is 'connected' on success, or 'failed'/'error'/'cancelled'/etc on failure.
  const [redirectStatus] = useState(
    () => new URLSearchParams(window.location.search).get('status'),
  )

  const [error, setError] = useState<string | null>(null)
  const [stillProcessing, setStillProcessing] = useState(false)
  // Guards the one-shot terminal handler so it fires exactly once.
  const handledRef = useRef(false)

  // Log the redirect params + stored state on mount so the full picture is
  // visible in DevTools at the start of the callback page lifecycle.
  useEffect(() => {
    // debug: bank-connection flow trace (DCP-216)
    lsDebug('callback:mount', { redirectStatus, connectionId })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Determine whether the redirect itself indicates a failure.
  // We evaluate this once, synchronously, via a lazy initialiser so the
  // error state is set before the first render rather than in an effect.
  const [redirectFailed] = useState(() => {
    if (!redirectStatus) return false
    return !SUCCESS_STATUSES.has(redirectStatus.toLowerCase())
  })

  // Apply redirect-level errors from state (avoids calling setError during render).
  useEffect(() => {
    if (redirectFailed) {
      const error = 'The connection was not completed. Please try connecting again.'
      // debug: bank-connection flow trace (DCP-216)
      lsDebugError('callback:error', { error })
      setError(error)
      return
    }
    // Guard: we need a stored connection to poll. If the redirect was successful
    // (or absent) but storage was cleared / different tab, we cannot poll.
    if (!connectionId) {
      const error = 'No connection in progress was found. Please try connecting again.'
      // debug: bank-connection flow trace (DCP-216)
      lsDebugError('callback:error', { error })
      setError(error)
    }
  }, [redirectFailed, connectionId])

  /**
   * Poll the connection status.
   * Only active when there is a stored connectionId and no error has been set.
   */
  const { data: status } = useLedgerSyncConnectionStatus(connectionId, !!connectionId && !error)

  /** Navigate back to the stored return path (or banking dashboard fallback). */
  const navigateBack = useCallback(() => {
    clearLedgerSyncOAuthState()
    if (returnPath) {
      navigate(returnPath, { replace: true })
    } else {
      navigate('/banking/dashboard', { replace: true })
    }
  }, [navigate, returnPath])

  // React to terminal connection states from the poller.
  useEffect(() => {
    if (handledRef.current || !status) return

    if (status.status === 'active') {
      handledRef.current = true
      // debug: bank-connection flow trace (DCP-216)
      lsDebug('callback:terminal', { status: status.status })
      // Await query invalidation before toasting + navigating so the screen we
      // return to — Banking Settings by default since DCP-324, or whatever
      // `returnPath` was stored — does not render with stale connection/account
      // data. A useEffect callback can't be async directly, so an async IIFE.
      void (async () => {
        // DCP-324: the org-level Bank Connections caches — the key space
        // `/settings/financial-accounts` (this page's default `returnPath`) actually reads.
        // A connection is an ORGANIZATION-level object, so there is no
        // facility-scoped list to invalidate here. Without these the redirect
        // flow lands back on a list still holding the pre-connect rows, and the
        // new bank appears only after a manual reload.
        //
        // Invalidated by PREFIX on purpose: the list is keyed by its sort
        // (`bankConnections(orgId, column, direction)`) and each accounts table
        // by its whole filter+sort+cursor set, so an exact key would match
        // nothing as soon as the user had sorted the table.
        await queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allBankConnections })
        await queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allConnectionAccounts })
        // The org-wide LedgerSync connection list. Currently reader-less —
        // Reconnect stopped needing the raw `institution_id` when it moved to
        // reauthorize (DCP-324) — but kept in step with the other two
        // invalidations of this key; see the note in `useLedgerSync.ts`.
        await queryClient.invalidateQueries({
          queryKey: LEDGERSYNC_KEYS.connections(currentOrganization?.id),
        })
        // DCP-260: this page is the SECOND terminal handler of the same
        // `connection.active` transition — the OAuth-redirect flow lands here
        // instead of `BankConnectionsSection`, so `useReconcileLedgerSyncConnection`
        // (which carries the matching invalidation) may never mount for this
        // connection. A LedgerSync account IS a `bank_accounts` row (DCP-217
        // collapse), and the org-wide caches under ['banking','bank-accounts']
        // are a different key space from LEDGERSYNC_KEYS — without this the
        // Settings → Facilities "Bank Accounts" count and the Dashboard account
        // filter keep the pre-connect number for the full 5-minute staleTime.
        await queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allBankAccounts })
        // DCP-374: a freshly connected card lands on the Credit Card
        // Availability page as soon as somebody assigns it a facility, and its
        // terms are written by the very finalize this transition triggers.
        await queryClient.invalidateQueries({
          queryKey: BANKING_KEYS.allCreditCardAvailability,
        })
        toast.success('Financial account connected successfully')
        navigateBack()
      })()
    } else if (status.status === 'failed' || status.status === 'disconnected') {
      handledRef.current = true
      // debug: bank-connection flow trace (DCP-216)
      lsDebug('callback:terminal', { status: status.status })
      toast.error('Financial account connection failed. Please try again.')
      navigateBack()
    }
  }, [status, currentOrganization?.id, queryClient, navigateBack])

  // Surface a "still processing" message if the server-side finalisation is slow.
  useEffect(() => {
    if (!connectionId || error) return
    const timer = setTimeout(() => {
      // debug: bank-connection flow trace (DCP-216)
      lsDebug('callback:still-processing', { connectionId })
      setStillProcessing(true)
    }, FINALIZE_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [connectionId, error])

  if (error) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-6">
        <AlertCircle className="h-10 w-10 text-destructive" />
        <p className="max-w-sm text-center text-sm text-destructive">{error}</p>
        <Button variant="default" onClick={navigateBack}>
          Try Again
        </Button>
      </div>
    )
  }

  if (stillProcessing) {
    // COPY IS DESTINATION-NEUTRAL on purpose. `navigateBack` goes wherever
    // `returnPath` says — Banking Settings today (the default written by
    // create-widget/reauthorize), the Banking dashboard when no state survived
    // the redirect, and a facility-scoped screen once DCP-383 adds one. Naming
    // one of those in the copy would be wrong for the others, and the
    // alternative (a path→label map) is a second router that has to be kept in
    // step with the first. "Leave this page" / "Go back" is true of all of them.
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-6">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="max-w-sm text-center text-sm text-muted-foreground">
          The connection is still being processed. You can leave this page — its accounts &amp;
          cards will appear once it completes.
        </p>
        <Button variant="outline" onClick={navigateBack}>
          Go back
        </Button>
      </div>
    )
  }

  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      <p className="text-sm text-muted-foreground">Finalizing the connection...</p>
    </div>
  )
}
